import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User } from 'configcat-react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { Userpilot } from 'userpilot';
import { useGetEmailCampaigns } from '@juno/client-api';
import {
  AXIOS_INSTANCE,
  useGetAMSFeatureConfigPublic,
  useGetCommunityGroups,
  useGetCommunityGroupsHierarchyAdmin,
  useGetCommunityGroupsHierarchyMember,
  useGetCourses,
  useGetFeatureConfigsPublic,
  useGetMe,
  useGetMyContentReports,
  useGetNavigationItems,
  useGetProfileWidget,
  useGetProfileWidgets,
  useGetQuickLinks,
  useGetSidebarWidgets,
  useGetUserSettings,
  useLoginUser,
  useUpdateUser,
} from '@juno/client-api';
import { EmailCampaign, Type8c9Enum } from '@juno/client-api/model';
import { FeatureConfigTypeEnum, JunoUser, RoleEnum } from '@juno/client-api/model';
import { FeatureConfig } from '@juno/client-api/utils';
import { ANALYTICS_CONFIGURATION } from '@juno/constants';
import {
  DefaultContext,
  MutationAction,
  defaultContextObject,
  logoutUser,
  onMutation,
  useBreakpoint,
} from '@juno/utils';
import { PlatformContext } from './PlatformContext';

interface UserPilotData {
  name: string;
  email: string;
  created_at: Date;
  id: string;
  company: {
    id: string;
    name: string;
    learning: boolean;
    library: boolean;
    mentorship: boolean;
    messaging: boolean;
    url: string;
    ams: string;
    amsTenant: string;
    dailyDigest: boolean;
  };
  site_admin: boolean;
  group_admin: boolean;
}

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [reportContent, setReportContent] = useState({
    reportContentDialogOpen: false,
  });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => !!localStorage.getItem('loggedInPlatform'),
  );
  const loginUser = useLoginUser();

  useEffect(() => {
    Userpilot.initialize(process.env.NX_USERPILOT_TOKEN || '');
  }, []);

  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      const callbackPath = platformContext?.site?.slug
        ? `/${platformContext.site.slug}/callback`
        : '/callback';
      if (
        window.location.pathname !== callbackPath &&
        !sessionStorage.getItem('postAuthRedirectUrl')
      ) {
        sessionStorage.setItem('postAuthRedirectUrl', window.location.href);
      }

      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  const platformContext = useContext(PlatformContext);
  useEffect(() => {
    if (auth.isAuthenticated && !!platformContext?.platform?.id) {
      if (!localStorage.getItem('loggedInPlatform')) {
        // Initiate Login
        loginUser
          .mutateAsync({
            platformId: platformContext?.platform?.id || '',
            data: {
              token: auth?.user?.access_token || '',
            },
          })
          .then((response) => {
            localStorage.setItem('loggedInPlatform', platformContext?.platform?.id || '');
            setIsLoggedIn(true);
          })
          .catch((reason) => {
            console.error('Failed to login user');
            logoutUser(platformContext?.platform?.id || '');
          });
      }
    }
  }, [auth, platformContext]);

  const { data: me, refetch: refetchMe } = useGetMe(
    platformContext?.platform?.id || '',
    {
      include: 'session_scheduled_attendees,saved_users',
    },
    { query: { enabled: !!platformContext?.platform && auth.isAuthenticated && isLoggedIn } },
  );

  const { data: featureConfigs, refetch: refetchFeatureConfigsPublic } = useGetFeatureConfigsPublic(
    platformContext?.site?.id || '',
  );
  const { data: amsConfig, refetch: refetchAMSConfigPublic } = useGetAMSFeatureConfigPublic(
    platformContext?.site?.id || '',
  );
  const { data: quickLinks } = useGetQuickLinks(
    platformContext?.site?.id || '',
    {},
    { query: { enabled: !!me } },
  );
  const { data: sidebarWidgets } = useGetSidebarWidgets(
    platformContext?.site?.id || '',
    { order: 'order' },
    { query: { enabled: !!me } },
  );
  const { data: profileWidgets, refetch: refetchProfileWidgets } = useGetProfileWidgets(
    platformContext?.site?.id || '',
    { order: 'order' },
    { query: { enabled: !!me } },
  );
  const { data: navItems, isLoading: navItemsLoading } = useGetNavigationItems(
    platformContext?.site?.id || '',
    { order: 'order' },
    { query: { enabled: !!me } },
  );
  const { data: contentReports } = useGetMyContentReports(
    platformContext?.site?.id || '',
    {},
    { query: { enabled: !!me } },
  );

  const { data: currentUserSettings } = useGetUserSettings(
    platformContext?.site?.id || '',
    {
      filter: { user__id: me?.id },
    },
    { query: { enabled: !!platformContext?.site?.id && !!me?.id } },
  );

  useEffect(() => {
    const siteTitle = FeatureConfig.getThemeConfigTheme(featureConfigs)?.siteTitle;
    if (siteTitle && siteTitle !== document.title) {
      document.title = siteTitle;
    }
  }, [featureConfigs]);

  const { xs, sm, md } = useBreakpoint();
  const isSmallScreen = xs || sm || md;
  // layout state
  const [showMessenger, setShowMessenger] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(true);

  const enableContentQueries = !!platformContext?.site?.id && !!me && isLoggedIn;

  const currentDate = useMemo(() => new Date().toISOString(), [showSideMenu]);
  const { data: myGroups } = useGetCommunityGroups(
    platformContext?.site?.id || '',
    {
      filter: {
        id__in: me?.community_groups_member || [],
        date_released__lte: currentDate,
      },
      filter_or: {
        date_end__gte: currentDate,
        date_end__isnull: true,
      },
    },
    { query: { enabled: enableContentQueries } },
  );

  const { data: myCourses } = useGetCourses(
    platformContext?.site?.id || '',
    {
      filter: { enrollments__user__id: me?.id || [], enrollments__date_completed__isnull: true },
    },
    { query: { enabled: enableContentQueries } },
  );

  const { data: impliedAdminOfGroups } = useGetCommunityGroupsHierarchyAdmin(
    platformContext?.site?.id || '',
    { query: { enabled: !!me } },
  );
  const { data: impliedMemberOfGroups } = useGetCommunityGroupsHierarchyMember(
    platformContext?.site?.id || '',
    { query: { enabled: !!me } },
  );
  const isAdmin = me?.role === RoleEnum.NUMBER_1 || me?.role === RoleEnum.NUMBER_2;
  const isStaff = me?.role === RoleEnum.NUMBER_2;

  const downloadUrl = (url: string) => {
    // TODO: This will probably need updated once we begin building vnext apps, but that's for another push
    window.open(url, '_blank');
  };

  const getImageSrc = (url: string) => {
    // TODO: This will probably need updated once we begin building vnext apps, but that's for another push
    return url;
  };

  const openExternalUrl = (url: string, target?: '_blank' | '_self' | '_parent' | '_top') => {
    // TODO: This will probably need updated once we begin building vnext apps, but that's for another push
    window.open(url, target);
  };

  const userObject = new User(me?.id || '', me?.email || '', '', {
    platform: platformContext?.platform?.id || '',
  });

  const enableLearning = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.feature_flag,
  )?.config?.learning;
  const enableLibrary = !!featureConfigs?.find((c) => c.type === FeatureConfigTypeEnum.feature_flag)
    ?.config?.library;
  const enableMentorship = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.feature_flag,
  )?.config?.mentorship;
  const restrictGZMVPFeatures = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.feature_flag,
  )?.config?.restrict_gz_mvp;
  const enable_messenger = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.user_and_profile,
  )?.config?.enable_messenger;
  const enable_private_meetings = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.user_and_profile,
  )?.config?.enable_private_meetings;
  const enable_emails = !!featureConfigs?.find(
    (c) => c.type === FeatureConfigTypeEnum.user_and_profile,
  )?.config?.enable_emails;

  const appSettings = {
    isWidget: false,
    isClient: true,
    isAdmin,
    isStaff,
    siteId: platformContext?.site?.id || '',
    user: me,
    refetchMe,
    setCurrentUser: () => {},
    site: platformContext?.site || undefined,
    setCurrentSite: () => {},
    onNavigate: undefined, // We can't use this in the app, so we'll instantiate it when we use it with a conditional
    downloadUrl: downloadUrl,
    getImageSrc: () => '',
    parseLinksFromDescription: () => {}, // unused in vnext
    openExternalUrl: openExternalUrl,
    sitePath: platformContext?.site?.slug || '',
    firehoseConfigurations: ANALYTICS_CONFIGURATION,
    preferredTags: [], // Making this empty array because we will have all tags accessible in client, plus tags are now auto synced from vnow to vnext
    isApp: false, // until we support full vnext app
    junoPlatformId: platformContext?.platform?.id || '',
    configs: featureConfigs,
    refetchFeatureConfigsPublic,
    amsConfig: amsConfig,
    refetchAMSConfigPublic,
    platformId: platformContext?.platform?.id || '',
    auth,
    showMessenger,
    setShowMessenger,
    platform: platformContext?.platform,
    showSideMenu,
    setShowSideMenu,
    myGroups,
    myCurrentCourses: myCourses,
    isSmallScreen,
    quickLinks: quickLinks || [],
    navItems: navItems || [],
    profileWidgets: profileWidgets || [],
    refetchProfileWidgets,
    navItemsLoading,
    impliedAdminOfGroups,
    impliedMemberOfGroups,
    sidebarWidgets: sidebarWidgets || [],
    enableLearning,
    enableLibrary,
    enableMentorship,
    restrictGZMVPFeatures,
    reportContent,
    setReportContent,
    myContentReports: contentReports || [],
    currentUserSettings: currentUserSettings?.find((us) => us.user === me?.id) || undefined,
    featureConfigs: {
      user_and_profile: {
        enableMessenger: enable_messenger,
        enablePrivateMeetings: enable_private_meetings,
        enableEmails: enable_emails,
      },
    },
  };

  const messengerDisabled = FeatureConfig.getThemeConfig(featureConfigs)?.config?.hide_messenger;

  const urlToReport = window.location.href
    .replace('https://', '')
    .replace('http://', '')
    .split('/')
    .slice(0, 2)
    .join('/');

  const updateUser = useUpdateUser(onMutation(MutationAction.UPDATE, '', refetchMe));
  useEffect(() => {
    if (auth.isAuthenticated && isLoggedIn && !!me?.id && (!me.timezone || me.timezone === '')) {
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      updateUser.mutate({
        platformId: platformContext?.site?.platform_id || '',
        userId: me?.id || '',
        data: {
          timezone: currentTimezone,
        } as JunoUser,
      });
    }
  }, [auth.isAuthenticated, me, platformContext?.site]);

  const hasReportedToUserPilot = useRef(false);

  // Get email campaigns to check Daily Digest status
  const { data: emailCampaigns } = useGetEmailCampaigns(platformContext?.site?.id || '', {
    query: { enabled: !!platformContext?.site?.id },
  });

  useEffect(() => {
    if (
      auth.isAuthenticated &&
      isLoggedIn &&
      me &&
      !hasReportedToUserPilot.current &&
      (me?.role === RoleEnum.NUMBER_1 ||
        me?.role === RoleEnum.NUMBER_2 ||
        me?.community_groups_admin?.length > 0) &&
      platformContext?.site &&
      featureConfigs &&
      amsConfig &&
      emailCampaigns
    ) {
      const dailyDigestCampaign = emailCampaigns?.find(
        (campaign: EmailCampaign) => campaign.type === Type8c9Enum.DAILY_DIGEST,
      );

      const userPilotData: UserPilotData = {
        name: `${me.first_name} ${me.last_name}`,
        email: me.email,
        created_at: new Date(),
        id: me.id,
        company: {
          id: platformContext?.site?.id || '',
          name: `${platformContext?.platform?.name} | ${platformContext?.site?.slug || ''}`,
          learning: enableLearning,
          library: enableLibrary,
          mentorship: enableMentorship,
          messaging: !messengerDisabled,
          url: urlToReport,
          ams: amsConfig.ams,
          amsTenant: amsConfig.tenant_id,
          dailyDigest: dailyDigestCampaign?.is_active || false,
        },
        site_admin: me.role === RoleEnum.NUMBER_1 || me?.role === RoleEnum.NUMBER_2,
        group_admin: me.community_groups_admin.length > 0,
      };
      hasReportedToUserPilot.current = true;
      Userpilot.identify(me.id, userPilotData);
    }
  }, [
    auth.isAuthenticated,
    me,
    platformContext?.site,
    featureConfigs,
    amsConfig,
    isLoggedIn,
    emailCampaigns,
  ]);

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <div>Signing you in...</div>;
    case 'signoutRedirect':
      return <div>Signing you out...</div>;
  }

  if (auth.isLoading) {
    return <div></div>;
  }

  if (auth.error) {
    return <div>Authentication Error... {auth.error.message}</div>;
  }

  if (auth.isAuthenticated && isLoggedIn) {
    AXIOS_INSTANCE.defaults.headers.common['Authorization'] = 'Bearer ' + auth?.user?.access_token;

    const postAuthRedirectUrl = sessionStorage.getItem('postAuthRedirectUrl');
    const postAuthRedirectTimeout = sessionStorage.getItem('postAuthRedirectUrlTimeout');

    if (postAuthRedirectTimeout && Date.now() > parseInt(postAuthRedirectTimeout)) {
      // Redirect timeout has expired. Do nothing, clear stored values.
      sessionStorage.removeItem('postAuthRedirectUrl');
      sessionStorage.removeItem('postAuthRedirectUrlTimeout');
    } else if (postAuthRedirectUrl) {
      sessionStorage.removeItem('postAuthRedirectUrl');
      sessionStorage.removeItem('postAuthRedirectUrlTimeout');
      // Clean up parameters that should only be used pre-auth
      const cleanUrl = new URL(postAuthRedirectUrl);
      if (cleanUrl.searchParams.get('is_admin_login')) {
        cleanUrl.searchParams.delete('is_admin_login');
      }
      if (cleanUrl.searchParams.get('is_staff_login')) {
        cleanUrl.searchParams.delete('is_staff_login');
      }
      window.location.href = cleanUrl.href;
    }

    return (
      <DefaultContext.Provider value={{ ...defaultContextObject, ...appSettings }}>
        {children}
      </DefaultContext.Provider>
    );
  } else {
    // This shouldn't happen... because if they aren't logged in they should get redirected
    return <div></div>;
  }
};

export default AppProvider;
